import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Brain, 
  Shield, 
  Database, 
  Scale, 
  Users, 
  AlertTriangle, 
  ExternalLink,
  FileText,
  Settings,
  ArrowLeft
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const AITransparency = () => {
  return (
    <>
      <Helmet>
        <title>AI Transparency - PaperVerse</title>
        <meta name="description" content="Learn how PaperVerse uses AI to generate paper reviews, including data processing details and your rights under GDPR Article 22." />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="flex-grow container max-w-4xl mx-auto pt-32 pb-16 px-4">
          <div className="mb-6">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-1">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Brain className="h-8 w-8 text-purple-600" />
              AI Transparency
            </h1>
            <p className="text-muted-foreground">
              GDPR Article 22 - Information about Automated Decision-Making
            </p>
          </div>

          <Alert className="mb-8 border-purple-200 bg-purple-50 dark:bg-purple-900/20">
            <Brain className="h-4 w-4 text-purple-600" />
            <AlertTitle>Meaningful Information About AI Processing</AlertTitle>
            <AlertDescription>
              Under GDPR Article 22, you have the right to meaningful information about the 
              logic involved in automated decision-making that affects you. This page provides 
              that information for PaperVerse's AI review system.
            </AlertDescription>
          </Alert>

          <div className="space-y-6">
            {/* What AI We Use */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  What AI Model We Use
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Model Provider</h4>
                    <p className="text-sm text-muted-foreground">Google DeepMind</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Model Family</h4>
                    <p className="text-sm text-muted-foreground">Google Gemini family (current generation)</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Gemini is a large language model (LLM) trained by Google. It processes text 
                  and can analyze academic papers to provide structured feedback on various aspects 
                  of research quality.
                </p>
              </CardContent>
            </Card>

            {/* How Decisions Are Made */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5" />
                  How AI Reviews Are Generated
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ol className="space-y-3">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">1</span>
                    <div>
                      <p className="font-medium">Paper Submission</p>
                      <p className="text-sm text-muted-foreground">Your paper's title, abstract, and PDF content are extracted</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">2</span>
                    <div>
                      <p className="font-medium">AI Analysis</p>
                      <p className="text-sm text-muted-foreground">The AI evaluates multiple criteria: methodology, clarity, novelty, relevance, technical rigor, and impact</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">3</span>
                    <div>
                      <p className="font-medium">Structured Output</p>
                      <p className="text-sm text-muted-foreground">The AI generates ratings (1-10), strengths, weaknesses, and improvement suggestions</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">4</span>
                    <div>
                      <p className="font-medium">Review Publication</p>
                      <p className="text-sm text-muted-foreground">The review is stored and displayed, clearly labeled as AI-generated</p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>

            {/* Writer */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  How PaperVerse Writer Generates Text
                </CardTitle>
                <CardDescription>
                  Writer is a generative assistant, not an assessment. It changes your manuscript only where you ask it to.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ol className="space-y-3">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">1</span>
                    <div>
                      <p className="font-medium">Your Instruction</p>
                      <p className="text-sm text-muted-foreground">You describe in plain language what should be written or changed</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">2</span>
                    <div>
                      <p className="font-medium">Context and Tools</p>
                      <p className="text-sm text-muted-foreground">
                        The assistant reads the relevant parts of your manuscript and may query open bibliographic data
                        with a sanitised topical search term — no identity, no manuscript content, no personal data
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">3</span>
                    <div>
                      <p className="font-medium">Draft and Compile</p>
                      <p className="text-sm text-muted-foreground">Generated LaTeX is written into a new version of your document and compiled to PDF</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">4</span>
                    <div>
                      <p className="font-medium">You Decide</p>
                      <p className="text-sm text-muted-foreground">
                        AI-generated content is labelled as such (EU AI Act Art. 50). You review it, keep it, edit it
                        or roll back to an earlier version — you remain the author of your manuscript
                      </p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>



            {/* Data Processing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Data Processing Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-start p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Data Sent to AI</p>
                      <p className="text-sm text-muted-foreground">Paper title, abstract, and full text content</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-start p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Data Transfer Location</p>
                      <p className="text-sm text-muted-foreground">
                        AI processing: European Union / EEA (Google Cloud Vertex AI, EU multi-region) — no third-country transfer of content;
                        in exceptional support cases Google may access data from third countries on the basis of EU standard contractual clauses.
                        Literature search in PaperVerse Writer is the one outbound flow: a sanitised topical search term (no identity, no manuscript
                        content, no personal data) is sent from our EU servers to the open bibliographic database OpenAlex (USA).
                        See the <a href="/privacy-policy#sub-processors" className="text-primary hover:underline">sub-processor list</a>.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-start p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Provider Data Retention</p>
                      <p className="text-sm text-muted-foreground">
                        Under our data processing agreement with Google, content sent for processing is configured
                        not to be retained beyond the processing duration and not to be used to train Google's models.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-start p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Legal Basis</p>
                      <p className="text-sm text-muted-foreground">
                        For AI features you request yourself — PaperVerse Writer and reviews of your own manuscript —
                        performance of the contract, GDPR Article 6(1)(b). Where AI processing is not needed to deliver
                        a service you asked for, we rely on your consent (Article 6(1)(a)) or, for the operation and
                        security of the platform, on our legitimate interests (Article 6(1)(f)). You can object to AI
                        processing at any time in your settings.
                      </p>

                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Limitations */}
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader className="bg-amber-50 dark:bg-amber-900/20">
                <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <AlertTriangle className="h-5 w-5" />
                  Important Limitations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">•</span>
                    <span>AI reviews are <strong>not a substitute</strong> for human peer review</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">•</span>
                    <span>The AI may <strong>make mistakes</strong> or misunderstand context</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">•</span>
                    <span>Reviews should be used as <strong>feedback for improvement</strong>, not definitive assessments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">•</span>
                    <span>The AI has <strong>no specialized domain expertise</strong> beyond its training data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">•</span>
                    <span>Training data has a <strong>knowledge cutoff date</strong> and may not reflect recent research</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">•</span>
                    <span>Generated text and citations <strong>can be inaccurate or fabricated</strong> — verify every reference against the original source</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">•</span>
                    <span>Writer output is a <strong>drafting aid</strong>: you remain the author and are responsible for the final manuscript, including any disclosure your venue requires</span>
                  </li>
                </ul>

              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Your Rights Under GDPR
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <Users className="h-5 w-5 mb-2 text-primary" />
                    <h4 className="font-medium mb-1">Right to Human Review</h4>
                    <p className="text-sm text-muted-foreground">
                      You can request peer reviews from human researchers instead of or in addition to AI reviews
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <FileText className="h-5 w-5 mb-2 text-primary" />
                    <h4 className="font-medium mb-1">Right to Contest</h4>
                    <p className="text-sm text-muted-foreground">
                      You can dispute any AI-generated assessment by contacting us or leaving comments
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <Settings className="h-5 w-5 mb-2 text-primary" />
                    <h4 className="font-medium mb-1">Right to Opt Out</h4>
                    <p className="text-sm text-muted-foreground">
                      You can object to AI processing entirely in your account settings
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <Shield className="h-5 w-5 mb-2 text-primary" />
                    <h4 className="font-medium mb-1">Right to Explanation</h4>
                    <p className="text-sm text-muted-foreground">
                      Each AI review includes explanations for the ratings and feedback provided
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Button asChild>
                <Link to="/settings">
                  <Settings className="h-4 w-4 mr-2" />
                  Manage AI Settings
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/privacy-policy">
                  <FileText className="h-4 w-4 mr-2" />
                  Read Privacy Policy
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <a href="https://ai.google.dev/terms" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Google AI Terms
                </a>
              </Button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AITransparency;
